import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, Wallet, CalendarHeart, PawPrint, Plus, Pencil, Trash2, ShoppingBag, Scissors, BedDouble } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ReceiptModal from "../../components/ReceiptModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY_PET = { name: "", species: "Dog", breed: "", weight_kg: "", coat_type: "short", birthdate: "", vaccine_notes: "", allergies: "", notes: "" };
const STATUS_COLORS = { booked: "bg-blue-100 text-blue-700", confirmed: "bg-emerald-100 text-emerald-700", in_progress: "bg-amber-100 text-amber-700", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-600", no_show: "bg-red-100 text-red-600", reserved: "bg-blue-100 text-blue-700", checked_in: "bg-emerald-100 text-emerald-700", checked_out: "bg-slate-100 text-slate-600" };

export default function Portal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [pets, setPets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState({ appointments: [], reservations: [] });
  const [petDialog, setPetDialog] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [petForm, setPetForm] = useState(EMPTY_PET);
  const [receiptId, setReceiptId] = useState(null);

  const load = () => {
    api.get("/portal/me").then((r) => setMe(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/portal/pets").then((r) => setPets(r.data)).catch(() => {});
    api.get("/portal/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/portal/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const savePet = async () => {
    try {
      const payload = { ...petForm, weight_kg: parseFloat(petForm.weight_kg) || 0 };
      if (editingPet) {
        await api.put(`/portal/pets/${editingPet.id}`, payload);
        toast.success("Pet updated");
      } else {
        await api.post("/portal/pets", payload);
        toast.success("Pet added");
      }
      setPetDialog(false);
      setEditingPet(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deletePet = async (id) => {
    try { await api.delete(`/portal/pets/${id}`); toast.success("Pet removed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const c = me?.customer;
  const upcoming = bookings.appointments.filter((a) => ["booked", "confirmed"].includes(a.status));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="portal-greeting">Hi, {user?.name}</h1>
          <p className="text-sm text-slate-500">Manage your pets, bookings and orders.</p>
        </div>
        <Button className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => navigate("/book")} data-testid="portal-book-btn">
          <CalendarHeart className="w-4 h-4 mr-2" /> Book a service
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="stat-loyalty">
          <Award className="w-5 h-5 text-[#F4A261] mb-2" />
          <p className="font-display font-bold text-2xl">{c?.loyalty_points ?? 0}</p>
          <p className="text-xs text-slate-500">Paw Points</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="stat-credit">
          <Wallet className="w-5 h-5 text-[#2A9D8F] mb-2" />
          <p className="font-display font-bold text-2xl">{peso(c?.store_credit ?? 0)}</p>
          <p className="text-xs text-slate-500">Store Credit</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="stat-pets">
          <PawPrint className="w-5 h-5 text-[#E84855] mb-2" />
          <p className="font-display font-bold text-2xl">{pets.length}</p>
          <p className="text-xs text-slate-500">Pets</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="stat-upcoming">
          <CalendarHeart className="w-5 h-5 text-[#0E7C84] mb-2" />
          <p className="font-display font-bold text-2xl">{upcoming.length}</p>
          <p className="text-xs text-slate-500">Upcoming appointments</p>
        </div>
      </div>

      <Tabs defaultValue="pets">
        <TabsList className="bg-[#E2F5F5]" data-testid="portal-tabs">
          <TabsTrigger value="pets" data-testid="tab-pets">My Pets</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders &amp; Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="pets" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pets.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid={`pet-card-${p.name.toLowerCase()}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-bold text-lg">{p.name}</p>
                    <p className="text-sm text-slate-500">{p.breed || p.species} · {p.weight_kg}kg · <span className="capitalize">{p.weight_tier}</span></p>
                  </div>
                  <Badge className="bg-[#D9F7F8] text-[#0E7C84] hover:bg-[#D9F7F8]">{p.species}</Badge>
                </div>
                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  <p><span className="font-semibold">Vaccines:</span> {p.vaccine_notes || "—"}</p>
                  <p><span className="font-semibold">Allergies:</span> {p.allergies || "None"}</p>
                  <p><span className="font-semibold">Coat:</span> <span className="capitalize">{p.coat_type}</span></p>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => { setEditingPet(p); setPetForm({ ...EMPTY_PET, ...p }); setPetDialog(true); }} data-testid={`edit-pet-${p.name.toLowerCase()}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deletePet(p.id)} data-testid={`delete-pet-${p.name.toLowerCase()}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            <button onClick={() => { setEditingPet(null); setPetForm(EMPTY_PET); setPetDialog(true); }}
              className="border-2 border-dashed border-[#BFE8E9] rounded-2xl p-5 flex flex-col items-center justify-center text-slate-400 hover:border-[#0E7C84] hover:text-[#0E7C84] transition-colors min-h-[180px]" data-testid="add-pet-card">
              <Plus className="w-8 h-8 mb-2" /> Add a pet
            </button>
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-6 space-y-8">
          <div>
            <h3 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><Scissors className="w-4 h-4 text-[#E84855]" /> Grooming appointments</h3>
            {bookings.appointments.length === 0 && <p className="text-sm text-slate-500">No grooming appointments yet.</p>}
            <div className="space-y-2">
              {bookings.appointments.map((a) => (
                <div key={a.id} className="bg-white rounded-xl border border-[#BFE8E9] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`appt-${a.id.slice(0, 8)}`}>
                  <div>
                    <p className="font-semibold text-sm">{a.service_name} — {a.pet_name}</p>
                    <p className="text-xs text-slate-500">{fmtDate(a.date)} at {a.time} · Groomer: {a.groomer_name || "TBA"}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold text-[#0E7C84]">{peso(a.price)}</span>
                    <Badge className={`${STATUS_COLORS[a.status]} hover:${STATUS_COLORS[a.status]} capitalize`}>{a.status.replace("_", " ")}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><BedDouble className="w-4 h-4 text-[#2A9D8F]" /> Boarding stays</h3>
            {bookings.reservations.length === 0 && <p className="text-sm text-slate-500">No boarding reservations yet.</p>}
            <div className="space-y-2">
              {bookings.reservations.map((r) => (
                <div key={r.id} className="bg-white rounded-xl border border-[#BFE8E9] p-4" data-testid={`res-${r.id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-sm">{r.room_name} — {r.pet_name}</p>
                      <p className="text-xs text-slate-500">{fmtDate(r.check_in)} → {fmtDate(r.check_out)} · {r.nights} night/s</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display font-bold text-[#2A9D8F]">{peso(r.estimated_total)}</span>
                      <Badge className={`${STATUS_COLORS[r.status]} hover:${STATUS_COLORS[r.status]} capitalize`}>{r.status.replace("_", " ")}</Badge>
                    </div>
                  </div>
                  {r.activities?.length > 0 && (
                    <div className="mt-3 bg-[#E2F5F5] rounded-lg p-3 space-y-1">
                      <p className="text-xs font-semibold text-slate-600">Daily care log</p>
                      {r.activities.slice(0, 5).map((act) => (
                        <p key={act.id} className="text-xs text-slate-500 capitalize">
                          <span className="font-medium text-[#0E7C84]">{act.type}</span> · {act.notes || "done"} · {act.time?.slice(5, 16).replace("T", " ")}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 && <p className="text-sm text-slate-500">No orders yet. <button className="text-[#0E7C84] font-semibold" onClick={() => navigate("/shop")}>Visit the shop</button></p>}
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-[#BFE8E9] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`order-${o.receipt_no}`}>
                <div>
                  <p className="font-semibold text-sm font-mono">{o.receipt_no}</p>
                  <p className="text-xs text-slate-500">{fmtDate(o.created_at)} · {o.items.length} item/s · {o.channel === "online" ? "Online order" : "In-store"}</p>
                </div>
                <div className="flex items-center gap-3">
                  {o.fulfillment === "pending_pickup" && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">For pickup</Badge>}
                  <span className="font-display font-bold text-[#0E7C84]">{peso(o.total)}</span>
                  <Button variant="outline" size="sm" onClick={() => setReceiptId(o.id)} data-testid={`view-receipt-${o.receipt_no}`}>
                    <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Receipt
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={petDialog} onOpenChange={setPetDialog}>
        <DialogContent data-testid="portal-pet-dialog">
          <DialogHeader><DialogTitle className="font-display">{editingPet ? "Edit pet" : "Add a pet"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={petForm.name} onChange={(e) => setPetForm({ ...petForm, name: e.target.value })} data-testid="pet-form-name" /></div>
            <div>
              <Label>Species</Label>
              <Select value={petForm.species} onValueChange={(v) => setPetForm({ ...petForm, species: v })}>
                <SelectTrigger data-testid="pet-form-species"><SelectValue /></SelectTrigger>
                <SelectContent>{["Dog", "Cat", "Bird", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Breed</Label><Input value={petForm.breed} onChange={(e) => setPetForm({ ...petForm, breed: e.target.value })} data-testid="pet-form-breed" /></div>
            <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={petForm.weight_kg} onChange={(e) => setPetForm({ ...petForm, weight_kg: e.target.value })} data-testid="pet-form-weight" /></div>
            <div>
              <Label>Coat type</Label>
              <Select value={petForm.coat_type} onValueChange={(v) => setPetForm({ ...petForm, coat_type: v })}>
                <SelectTrigger data-testid="pet-form-coat"><SelectValue /></SelectTrigger>
                <SelectContent>{["short", "long", "double", "matted", "hairless"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Birthdate</Label><Input type="date" value={petForm.birthdate} onChange={(e) => setPetForm({ ...petForm, birthdate: e.target.value })} data-testid="pet-form-birthdate" /></div>
            <div className="col-span-2"><Label>Vaccination records</Label><Input value={petForm.vaccine_notes} onChange={(e) => setPetForm({ ...petForm, vaccine_notes: e.target.value })} placeholder="e.g. Anti-rabies until Mar 2027, 5-in-1 done" data-testid="pet-form-vaccines" /></div>
            <div className="col-span-2"><Label>Allergies</Label><Input value={petForm.allergies} onChange={(e) => setPetForm({ ...petForm, allergies: e.target.value })} data-testid="pet-form-allergies" /></div>
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={savePet} disabled={!petForm.name} data-testid="pet-form-save">
            {editingPet ? "Save changes" : "Add pet"}
          </Button>
        </DialogContent>
      </Dialog>

      <ReceiptModal saleId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} />
    </div>
  );
}
